import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import api from "../../api/api";

type LeavePolicyForm = {
  deduction: boolean;
  carryForward: boolean;
  daysAllowable: number;
  hourly: boolean;
  monthly: boolean;
  sunday: boolean;
  saturday: boolean;
  lateDeduction: boolean;
  overtimeDeduction: boolean;
  rateofOvertime: number;
};

const DEFAULT_FORM: LeavePolicyForm = {
  deduction: false,
  carryForward: false,
  daysAllowable: 0,
  hourly: false,
  monthly: true,
  sunday: true,
  saturday: false,
  lateDeduction: false,
  overtimeDeduction: false,
  rateofOvertime: 1.0,
};

const LeavePolicySettings = () => {
  // `form` still carries lateDeduction / carryForward / daysAllowable /
  // overtimeDeduction / rateofOvertime even though they no longer have controls
  // here — Settings → Policy Rules owns them now. They are kept in state (and
  // posted back unchanged) because this endpoint REPLACES the whole settings
  // document: dropping them would blank the columns that un-migrated branches
  // still read through the Phase 1 legacy bridge.
  const [form, setForm] = useState<LeavePolicyForm>(DEFAULT_FORM);
  // Holidays now live on the Leave Management page, but the backend save
  // endpoint replaces the whole settings document — preserve the existing
  // holiday_dates so we don't wipe them when only the policy changes.
  const [holidayDates, setHolidayDates] = useState<any[]>([]);
  const [allowance, setAllowance] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const clearMessage = () => {
    setMessage("");
    setIsSuccess(false);
  };

  const fetchSettings = async () => {
    try {
      setIsInitialLoading(true);
      const { data } = await api.get("/salary/get_salary_settings");
      if (!data?.success) return;
      const d = data.data;
      setForm({
        deduction: d.deduction ?? false,
        carryForward: d.carryForward ?? false,
        daysAllowable: d.daysAllowable ?? 0,
        hourly: d.hourly ?? false,
        monthly: d.monthly ?? true,
        sunday: d.sunday ?? false,
        saturday: d.saturday ?? false,
        lateDeduction: d.lateDeduction ?? false,
        overtimeDeduction: d.overtimeDeduction ?? false,
        rateofOvertime: d.rateofOvertime ?? 1.0,
      });
      setHolidayDates(d.holiday_dates ?? []);
      setAllowance(d.allowance ?? false);
    } catch (err) {
      console.error("Failed to fetch leave policy", err);
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleHourlyMonthlyChange = (checked: boolean, isHourly: boolean) => {
    setForm((prev) => ({
      ...prev,
      hourly: isHourly ? checked : false,
      monthly: isHourly ? false : checked,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setMessage("");
    try {
      await api.post("/salary/create_or_update_salary_settings", {
        ...form,
        allowance,
        holiday_dates: holidayDates,
      });
      setMessage("Successfully saved leave policy!");
      setIsSuccess(true);
      fetchSettings();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Failed to save leave policy";
      setMessage(detail);
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="gt-eyebrow-row">
          <span className="gt-leaf" />
          <span className="gt-eyebrow">Company · iv. Leave Policy</span>
        </div>
        <h2 className="text-2xl font-semibold mt-2">
          Leave <em className="gt-serif-italic">policy</em>
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Weekly offs and how payroll is calculated.
        </p>
      </div>

      <div className="flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 text-blue-900 rounded-xl p-3">
        <Info size={16} className="mt-0.5 shrink-0" />
        <p>
          Late-coming, overtime and leave rules have moved to{" "}
          <Link to="/settings/policy-rules" className="font-semibold underline">
            Settings → Policy Rules
          </Link>
          , where they can also be limited to certain dates or people.
        </p>
      </div>

      {/* Leave Policy */}
      <div className="p-6 border border-green-200 rounded-2xl bg-green-50 shadow-sm">
        <h3 className="text-lg font-bold mb-4 text-green-800">Policy</h3>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.deduction}
              onChange={(e) => setForm({ ...form, deduction: e.target.checked })}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Deduction</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.hourly}
              onChange={(e) => handleHourlyMonthlyChange(e.target.checked, true)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Hourly</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.monthly}
              onChange={(e) => handleHourlyMonthlyChange(e.target.checked, false)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Monthly</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.sunday}
              onChange={(e) => setForm({ ...form, sunday: e.target.checked })}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Sunday</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.saturday}
              onChange={(e) => setForm({ ...form, saturday: e.target.checked })}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Saturday</span>
          </label>
        </div>
      </div>

      {message && (
        <div
          className={`p-4 rounded-xl shadow-sm text-base font-semibold flex items-center justify-between ${
            isSuccess
              ? "bg-green-100 border-2 border-green-300 text-green-800"
              : "bg-red-100 border-2 border-red-300 text-red-800"
          }`}
        >
          <span>{message}</span>
          <button onClick={clearMessage} className="ml-3 text-lg hover:text-black">
            ×
          </button>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="gt-btn is-primary is-lg"
        >
          {isLoading ? "Saving..." : "Save Leave Policy"}
        </button>
      </div>
    </div>
  );
};

export default LeavePolicySettings;
